
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import {
    DueDiligenceDocument,
    DueDiligenceHolderSection,
    DueDiligenceRecord,
} from '@/store/dueDiligence/types';
import CPDueDiligenceDocumentsOverviewSkeletonLoader from './CPDueDiligenceDocumentsOverviewSkeletonLoader.vue';

@Component({
    name: 'CPDueDiligenceDocumentsOverview',
    components: {
        CPDueDiligenceDocumentsOverviewSkeletonLoader
    }
})
export default class CPDueDiligenceDocumentsOverview extends Vue {
    @Prop({ type: Number, required: true }) readonly riskCategory!: number;
    @Prop({ type: Number, required: true }) readonly dueDiligenceId!: number;

    private loading: boolean = false;

    public setLoading(value: boolean) {
        this.loading = value;
    }
    public get isLoading(): boolean {
        return this.loading;
    }

    private dueDiligenceDetails: DueDiligenceRecord | null = null;
    private holderSections: DueDiligenceHolderSection[] = [];
    private isPageLoading: boolean = true;

    async created() {
        this.isPageLoading = true;

        try {
            const [details, sections] = await Promise.all([
                this.getDueDiligenceDetails(),
                this.fetchRequiredDocuments(),
            ]);

            this.dueDiligenceDetails = details;
            this.holderSections = sections;
        } finally {
            this.isPageLoading = false;
        }
    }

    private async fetchRequiredDocuments(): Promise<DueDiligenceHolderSection[]> {
        return await this.$store.dispatch(
            'dueDiligence/getRequiredDocuments',
            this.dueDiligenceId,
        ) ?? [];
    }

    private async getDueDiligenceDetails() {
        await this.$store.dispatch('dueDiligence/getDueDiligenceById', this.dueDiligenceId);
        return (
            this.$store.getters['dueDiligence/getCurrentDueDiligence'] ??
            this.dueDiligenceDetails
        );
    }

    private sectionKey(section: DueDiligenceHolderSection): string {
        return section.UserInfoDetailsId == null
            ? 'shared'
            : `h${section.UserInfoDetailsId}`;
    }

    private fileInputRef(section: DueDiligenceHolderSection, typeId: number): string {
        return `fileInput_${this.sectionKey(section)}_${typeId}`;
    }

    private getLatestDocument(
        section: DueDiligenceHolderSection,
        typeId: number,
    ): DueDiligenceDocument | null {
        const docs = section.UploadedDocuments.filter(
            (d) => d.DocumentTypeId === typeId,
        );
        return docs.length ? docs.sort((a, b) => b.Id - a.Id)[0] : null;
    }

    private getFileName(url: string): string {
        if (!url || url === 'N/A') return 'Unnamed Document';
        return url.split('/').pop() || 'Document';
    }

    private formatEmail(email: string): string {
        return email ? email.replace(/;/g, ', ') : '';
    }

    private formatDate(dateString: string): string {
        if (!dateString || dateString === 'N/A') return 'N/A';

        return new Date(dateString)
            .toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            })
            .replace(/\//g, '/');
    }

    private formatDateWithTime(dateString: string): string {
        if (!dateString || dateString === 'N/A') return 'N/A';

        const date = new Date(dateString);

        if (isNaN(date.getTime())) return 'N/A';

        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
            .toUpperCase()
            .replace(',', '');
    }

    public triggerUpload(section: DueDiligenceHolderSection, typeId: number): void {
        const refs = this.$refs[this.fileInputRef(section, typeId)] as HTMLInputElement[];
        refs?.[0]?.click();
    }

    public async onFileSelected(
        event: Event,
        section: DueDiligenceHolderSection,
        typeId: number,
    ): Promise<void> {
        const input = event.target as HTMLInputElement;
        this.setLoading(true);

        if (input.files?.length) {
            const file = input.files[0];

            const formData = new FormData();

            formData.append('DueDiligenceId', this.dueDiligenceId.toString());
            formData.append('Documents[0].DocumentTypeId', typeId.toString());
            formData.append('Documents[0].File', file);
            if (section.UserInfoDetailsId != null) {
                formData.append(
                    'Documents[0].UserInfoDetailsId',
                    section.UserInfoDetailsId.toString(),
                );
            }

            try {
                await this.$store.dispatch('dueDiligence/uploadDueDiligenceDocument', formData);
                this.holderSections = await this.fetchRequiredDocuments();
                input.value = '';
            } catch (error) {
                console.log("Upload failed", error);
            }
        }
        this.setLoading(false);
    }

    public async handleDownload(document: DueDiligenceDocument): Promise<void> {
        this.setLoading(true);
        await this.$store.dispatch(
            'dueDiligence/downloadDueDiligenceDocument',
            document.Id,
        );
        this.setLoading(false);
    }
}
